/**
 * InteractiveMessageList.tsx
 *
 * Displays quick-action interactive message buttons for templates.
 * Extracted from ChatInner.v2.tsx.
 */

import React, { FC } from "react"
import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { Collaboration } from "src/app/models/Chat"
import Language from "src/app/models/Language"
import Template from "src/app/models/Template"
import { getListTemplateInteractive } from "src/db/serviceTemplate"
import { getTemplateButtonText } from "src/util/Formatter"
import * as lc from "src/app/modules/localstorage/index"
import * as Log from "src/util/SDayslogger"
import InteractiveTemplate from "src/app/layout/chat-handle/components/chat-modal/InteractiveTemplate"

export interface InteractiveMessageListProps {
  companyId: string
  sendMessage: (templateLanguage?: Language) => void
  templateList?: Template[]
  collaboration?: Collaboration
}

export const InteractiveMessageList: FC<InteractiveMessageListProps> = ({
  companyId,
  sendMessage,
  templateList,
  collaboration,
}) => {
  const client = lc.getItemLC(lc.LCName.Client)
  const { i18n, t } = useTranslation()

  const { data: interactiveTemplates, isLoading } = useQuery(
    ["interactive-templates", i18n],
    async () => {
      if (!client) {
        Log.error("Interactive Message List: client not found")
        return []
      }

      const interactiveTemps = await getListTemplateInteractive(
        companyId,
        client.id,
        i18n.language,
        3
      )

      Log.debug(
        `found templateInteractiveList interactiveTemps #${interactiveTemps?.length}`
      )

      return interactiveTemps ?? []
    },
    {
      enabled: !templateList || templateList.length <= 0,
      keepPreviousData: true,
      cacheTime: 1000 * 60 * 30, // 30 minutes
    }
  )

  const templateInteractiveList =
    templateList && templateList.length > 0
      ? templateList
      : interactiveTemplates && interactiveTemplates?.length > 0
      ? interactiveTemplates
      : []

  Log.debug(`found templateInteractiveList templateList #${templateList?.length}`)
  Log.debug(
    `found templateInteractiveList interactiveTemplates#${interactiveTemplates?.length}`
  )

  if (isLoading) {
    return <div>loading...</div>
  }

  if (templateInteractiveList.length > 0) {
    return (
      <div
        data-testid="interactive-message-quick-options"
        className="position-relative bg-opacity-0 d-flex justify-content-start align-items-center p-0 m-0"
        style={{ gap: "0.5rem" }}
      >
        {templateInteractiveList.slice(0, 3).map(
          (template) =>
            template.languagesModel &&
            template.languagesModel.length > 0 && (
              <React.Fragment key={`${template.id}`}>
                <button
                  type="button"
                  className="button-interactive-chat p-1"
                  key={`interactive-chat-detail-${template.id}`}
                  data-testid={`interactive-chat-detail-${template.id}`}
                  data-bs-target={`#InteractiveTemplateModal${template.id}`}
                  data-bs-toggle="modal"
                >
                  <span>
                    {getTemplateButtonText(template.languagesModel[0], t)}
                  </span>
                </button>
                <InteractiveTemplate
                  list={templateInteractiveList}
                  template={template}
                  sendMessage={sendMessage}
                  collaboration={collaboration}
                />
              </React.Fragment>
            )
        )}

        <button
          type="button"
          key="others"
          className="button-interactive-chat p-1"
          data-testid="interactive-chat-detail-other"
          data-bs-target="#InteractiveTemplateModalOther"
          data-bs-toggle="modal"
        >
          <span>{t("Common.Others")}</span>
          <i className="bi bi-chevron-right" />
        </button>
        <InteractiveTemplate
          list={templateInteractiveList}
          sendMessage={sendMessage}
          collaboration={collaboration}
        />
      </div>
    )
  }

  return null
}

export default InteractiveMessageList
